import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type {
  ContractStage,
  ContractStageCounts,
  ContractSyncStatus,
  ContractWorkType,
  Project,
  SignedContract,
} from '@t3/shared';
import { FileSignature, ExternalLink, RefreshCw, Star, Stethoscope } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Select } from '../components/Select.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import { ViewsSidebar, type ViewDefinition } from '../components/ViewsSidebar.tsx';

interface OrgLite {
  id: string;
  name: string;
}

interface ContractsResponse {
  contracts: SignedContract[];
  counts: ContractStageCounts;
  sync: ContractSyncStatus;
}

interface ProbeResult {
  ok: boolean;
  requestPath: string;
  truncated?: boolean;
  body?: string;
  error?: string;
}

const STAGES: ContractStage[] = [
  'unsigned',
  'signed',
  'in_progress',
  'complete',
  'pending_payment',
  'paid',
  'closed',
];

const STAGE_LABEL: Record<ContractStage, string> = {
  unsigned: 'Unsigned',
  signed: 'Signed',
  in_progress: 'In progress',
  complete: 'Complete',
  pending_payment: 'Pending payment',
  paid: 'Paid',
  closed: 'Closed',
};

// Delivery stages read blue, money stages purple, closed greys out.
const STAGE_PILL: Record<ContractStage, string> = {
  // Unsigned is not yet work — grey, like closed, so the board reads as
  // "live work" at a glance rather than a wall of blue.
  unsigned: 'bg-[var(--color-surface)] text-[var(--color-muted)]',
  signed: 'bg-[var(--color-t3-blue)]/10 text-[var(--color-t3-blue)]',
  in_progress: 'bg-[var(--color-t3-blue)]/10 text-[var(--color-t3-blue)]',
  complete: 'bg-emerald-50 text-emerald-700',
  pending_payment: 'bg-amber-50 text-amber-800',
  paid: 'bg-[var(--color-t3-purple)]/10 text-[var(--color-t3-purple)]',
  closed: 'bg-[var(--color-surface)] text-[var(--color-muted)]',
};

const WORK_TYPE_LABEL: Record<ContractWorkType, string> = {
  block_hours: 'Block hours',
  project: 'Project',
  mnda: 'MNDA',
};

const VIEWS: ViewDefinition[] = [
  { id: 'open', label: 'Open contracts', search: 'openOnly=true' },
  { id: 'unsigned', label: 'Unsigned', search: 'stage=unsigned' },
  { id: 'follow-up', label: 'Following up', search: 'followUp=true' },
  { id: 'untriaged', label: 'Needs triage', search: 'untriaged=true' },
  { id: 'needs-project', label: 'Needs a project', search: 'needsProject=true' },
  { id: 'unlinked', label: 'No client linked', search: 'unlinked=true' },
  { id: 'in-progress', label: 'In progress', search: 'stage=in_progress' },
  { id: 'pending-payment', label: 'Pending payment', search: 'stage=pending_payment' },
  { id: 'all', label: 'All contracts', search: '' },
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const mins = Math.round((Date.now() - then) / 60_000);
  if (mins < 1) return 'just now';
  if (mins === 1) return '1 minute ago';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours === 1) return '1 hour ago';
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

export function ContractsListPage(): React.ReactElement {
  useDocumentTitle('Signed contracts');
  const { agent } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<ContractsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<OrgLite[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [probing, setProbing] = useState(false);
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [projectFor, setProjectFor] = useState<SignedContract | null>(null);
  const [clientFor, setClientFor] = useState<SignedContract | null>(null);

  const apiQuery = useMemo(() => {
    const p = new URLSearchParams();
    for (const key of [
      'stage',
      'workType',
      'organizationId',
      'untriaged',
      'unlinked',
      'needsProject',
      'openOnly',
      'followUp',
    ]) {
      const v = searchParams.get(key);
      if (v) p.set(key, v);
    }
    return p.toString();
  }, [searchParams]);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch<ContractsResponse>(
        `/api/contracts${apiQuery ? `?${apiQuery}` : ''}`,
      );
      setData(r);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load contracts.');
    }
  }, [apiQuery]);

  useEffect(() => {
    void load();
  }, [load]);

  // Org list is only needed for the "link a client" dropdowns, but those
  // appear inline on any unlinked row, so fetch it up front.
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ organizations: OrgLite[] }>('/api/organizations')
      .then((r) => {
        if (!cancelled) {
          setOrgs([...r.organizations].sort((a, b) => a.name.localeCompare(b.name)));
        }
      })
      .catch(() => {
        if (!cancelled) setOrgs([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function patch(contract: SignedContract, body: Record<string, unknown>) {
    setBusyId(contract.id);
    setRowError(null);
    try {
      await apiFetch(`/api/contracts/${contract.id}`, { method: 'PATCH', json: body });
      await load();
    } catch (err) {
      setRowError({
        id: contract.id,
        message: err instanceof ApiError ? err.message : 'Update failed.',
      });
    } finally {
      setBusyId(null);
    }
  }

  async function runSync() {
    setSyncing(true);
    setError(null);
    try {
      await apiFetch('/api/contracts/sync', { method: 'POST' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  }

  async function runProbe() {
    setProbing(true);
    setProbe(null);
    setError(null);
    try {
      setProbe(await apiFetch<ProbeResult>('/api/contracts/sync/probe', { method: 'POST' }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Connection test failed.');
    } finally {
      setProbing(false);
    }
  }

  if (!agent) return <p>Loading…</p>;

  // Billing-gated on read as well as write (migration 0083). The nav item
  // is hidden for everyone else, but a bookmarked or shared URL still
  // has to land somewhere honest rather than on an empty board — the
  // API returns 403 for these agents, which would otherwise read as
  // "there are no contracts".
  const canBilling = agent.role === 'admin' || agent.role === 'billing_admin';
  if (!canBilling) {
    return (
      <Card>
        <h1 className="text-xl font-semibold text-[var(--color-header)]">Signed contracts</h1>
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          This page is limited to billing admins. If you need access to what has been sold and
          where it stands, ask an admin to give you the <strong>Billing admin</strong> role.
        </p>
      </Card>
    );
  }

  const counts = data?.counts;
  const views: ViewDefinition[] = VIEWS.map((v) => {
    if (!counts) return v;
    if (v.id === 'unsigned') return { ...v, count: counts.byStage.unsigned };
    if (v.id === 'follow-up') return { ...v, count: counts.followUp };
    if (v.id === 'untriaged') return { ...v, count: counts.untriaged };
    if (v.id === 'needs-project') return { ...v, count: counts.needsProject };
    if (v.id === 'unlinked') return { ...v, count: counts.unlinked };
    if (v.id === 'in-progress') return { ...v, count: counts.byStage.in_progress };
    if (v.id === 'pending-payment') return { ...v, count: counts.byStage.pending_payment };
    return v;
  });

  const sync = data?.sync;
  const hasFilters = apiQuery.length > 0;

  // Sidebar views and these dropdowns write to the same query string, so
  // picking a stage here deselects a view that disagrees with it and
  // vice versa — one filter state, two ways in.
  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <ViewsSidebar title="Contract views" views={views} basePath="/admin/contracts" />

      <div className="min-w-0 flex-1 space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            {/* Only a full admin can open /admin — a billing admin reaches
                this board straight from the Admin menu, so a back link to a
                page they'd be refused would be a dead end. */}
            {agent.role === 'admin' && (
              <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
                ← Admin
              </Link>
            )}
            <h1 className="mt-1 text-3xl font-bold text-[var(--color-header)]">Signed contracts</h1>
            <p className="mt-1 max-w-2xl text-[var(--color-muted)]">
              Every contract a client has signed in Qwilr. Qwilr owns the signature; we own the
              stage and how the work gets delivered. Projects are never created automatically —
              use the button when the work is ready to start.
            </p>
          </div>
          {canBilling && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => void runProbe()}
                disabled={probing || !sync?.configured}
                title="Show exactly what Qwilr's API returns for this account"
              >
                <Stethoscope className="h-4 w-4" />
                {probing ? 'Testing…' : 'Test connection'}
              </Button>
              <Button
                variant="outline"
                onClick={() => void runSync()}
                disabled={syncing || !sync?.configured}
                title={
                  sync?.configured
                    ? 'Pull the latest signed contracts from Qwilr now'
                    : 'QWILR_API_TOKEN is not set'
                }
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing…' : 'Sync now'}
              </Button>
            </div>
          )}
        </header>

        <Card className="flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
              Stage
            </span>
            <select
              aria-label="Filter by stage"
              value={searchParams.get('stage') ?? ''}
              onChange={(e) => updateFilter('stage', e.target.value)}
              className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            >
              <option value="">All stages</option>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {STAGE_LABEL[s]}
                  {counts ? ` (${counts.byStage[s]})` : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
              Type
            </span>
            <select
              aria-label="Filter by work type"
              // "Not set" is workType=none, a plain "no type chosen"
              // filter. It is NOT the Needs-triage view: that one is a
              // work queue and hides unsigned contracts, which would be
              // a surprising thing for a type filter to do.
              value={searchParams.get('workType') ?? ''}
              onChange={(e) => updateFilter('workType', e.target.value)}
              className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            >
              <option value="">All types</option>
              <option value="block_hours">{WORK_TYPE_LABEL.block_hours}</option>
              <option value="project">{WORK_TYPE_LABEL.project}</option>
              <option value="mnda">{WORK_TYPE_LABEL.mnda}</option>
              <option value="none">Not set{counts ? ` (${counts.workTypeNone})` : ''}</option>
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
              Client
            </span>
            <select
              aria-label="Filter by client"
              value={searchParams.get('organizationId') ?? ''}
              onChange={(e) => updateFilter('organizationId', e.target.value)}
              disabled={orgs === null}
              className="max-w-[220px] rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            >
              <option value="">All clients</option>
              {(orgs ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>

          <div className="ml-auto flex items-center gap-3 text-sm">
            <span className="text-[var(--color-muted)]">
              {data ? `${data.contracts.length} of ${data.counts.total}` : '…'}
            </span>
            {hasFilters && (
              <button
                onClick={() => setSearchParams(new URLSearchParams())}
                className="text-[var(--color-t3-blue)] hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </Card>

        {sync && <SyncBanner sync={sync} />}

        {error && (
          <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
            <p className="text-sm text-[var(--color-danger)]">{error}</p>
          </Card>
        )}

        {probe && (
          <Card className={probe.ok ? '' : 'border-[var(--color-danger)]/30 bg-red-50/40'}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-[var(--color-header)]">
                  Qwilr connection test — {probe.ok ? 'responded' : 'failed'}
                </h3>
                <p className="mt-0.5 font-mono text-[11px] text-[var(--color-muted)]">
                  GET https://api.qwilr.com/v1{probe.requestPath}
                </p>
              </div>
              <button
                onClick={() => setProbe(null)}
                className="text-xs text-[var(--color-muted)] hover:underline"
              >
                Dismiss
              </button>
            </div>
            {probe.error ? (
              <p className="mt-2 text-sm text-[var(--color-danger)]">{probe.error}</p>
            ) : (
              <>
                <pre className="mt-2 max-h-80 overflow-auto rounded-[var(--radius-button)] bg-[var(--color-surface)] p-3 font-mono text-[11px] leading-relaxed">
                  {probe.body}
                </pre>
                {probe.truncated && (
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    Truncated at 20,000 characters.
                  </p>
                )}
              </>
            )}
          </Card>
        )}

        {!data ? (
          <p className="text-[var(--color-muted)]">Loading…</p>
        ) : data.contracts.length === 0 ? (
          <EmptyState
            icon={<FileSignature className="h-8 w-8" />}
            title="No signed contracts here"
            body={
              sync?.configured
                ? 'Nothing matches this view. Signed contracts appear within 15 minutes of being accepted in Qwilr.'
                : 'Qwilr API syncing is off (QWILR_API_TOKEN is not set). Contracts still arrive from the Qwilr webhook and the admin paste-import.'
            }
          />
        ) : (
          // The table has to fit beside the views sidebar: the page body is
          // 1200px wide (SHELL_WIDTH in App.tsx), less a 224px sidebar and
          // the 24px gap, so roughly 952px. It did not fit — a 960px floor
          // plus wide cells pushed Delivery, the column carrying the only
          // action on the row, off the right edge behind a scrollbar nobody
          // thinks to drag. The narrow padding, capped selects and short
          // Delivery strings below are all part of staying inside that
          // budget; measure before widening any of them
          // (tests/e2e/contracts-table-fits.spec.ts).
          <Card className="overflow-x-auto !p-0">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="py-3 pl-3 pr-0">
                    <span className="sr-only">Follow up</span>
                  </th>
                  <th className="px-3 py-3">Contract</th>
                  <th className="px-3 py-3">Client</th>
                  <th className="px-3 py-3">Created</th>
                  <th className="px-3 py-3">Stage</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3">Delivery</th>
                </tr>
              </thead>
              <tbody>
                {data.contracts.map((c) => (
                  <ContractRow
                    key={c.id}
                    contract={c}
                    orgs={orgs}
                    busy={busyId === c.id}
                    error={rowError?.id === c.id ? rowError.message : null}
                    canRelink={canBilling}
                    onPatch={(body) => void patch(c, body)}
                    onCreateProject={() => setProjectFor(c)}
                    onAddClient={() => setClientFor(c)}
                  />
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      {clientFor && (
        <AddClientDialog
          contract={clientFor}
          onClose={() => setClientFor(null)}
          onCreated={(org) => {
            setClientFor(null);
            // Keep the picker in sync without a second round-trip.
            setOrgs((prev) =>
              prev && !prev.some((o) => o.id === org.id)
                ? [...prev, org].sort((a, b) => a.name.localeCompare(b.name))
                : prev,
            );
            void load();
          }}
        />
      )}

      {projectFor && (
        <CreateProjectDialog
          contract={projectFor}
          orgs={orgs ?? []}
          onClose={() => setProjectFor(null)}
          onCreated={() => {
            setProjectFor(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function SyncBanner({ sync }: { sync: ContractSyncStatus }): React.ReactElement {
  if (!sync.configured) {
    return (
      <Card className="border-amber-300 bg-amber-50/40">
        <p className="text-sm text-amber-900">
          <strong>Qwilr API sync is off.</strong> <code>QWILR_API_TOKEN</code> is not set, so the
          15-minute pull is disabled. Contracts still arrive from the Qwilr webhook and from{' '}
          <Link to="/admin/qwilr-import" className="underline">
            the paste-URL importer
          </Link>
          . Set the token to turn automatic syncing on.
        </p>
      </Card>
    );
  }
  if (sync.last?.status === 'failed') {
    // A rejected token is not something waiting fixes, and it is not
    // something anyone can act on from an HTTP status dump. Say what
    // happened and where to go.
    if (sync.last.failureKind === 'auth') {
      return (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">
            <strong>Qwilr is rejecting our API token.</strong> Syncing has been failing since{' '}
            {formatRelative(sync.last.ranAt)} and will keep failing until the token is replaced —
            retrying will not help.
          </p>
          <p className="mt-2 text-sm text-[var(--color-body)]">
            Generate a new access token at{' '}
            <a
              href="https://app.qwilr.com/#/settings/api"
              target="_blank"
              rel="noreferrer"
              className="text-[var(--color-t3-blue)] underline"
            >
              Qwilr → Settings → API
            </a>
            , set <code>QWILR_API_TOKEN</code> and restart the backend. Qwilr shows a new token
            once and never again, so copy it before closing the dialog.
          </p>
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            Contracts already on this page are unaffected, and the Qwilr webhook and paste-importer
            keep working — only the automatic pull is down.
          </p>
          <p className="mt-2 font-mono text-[11px] text-[var(--color-muted)]">{sync.last.error}</p>
        </Card>
      );
    }
    return (
      <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
        <p className="text-sm text-[var(--color-danger)]">
          <strong>Last sync failed</strong> {formatRelative(sync.last.ranAt)}: {sync.last.error}
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          The next scheduled sync will try again in under {sync.intervalMinutes} minutes.
        </p>
      </Card>
    );
  }
  return (
    <p className="text-xs text-[var(--color-muted)]">
      Syncing from Qwilr every {sync.intervalMinutes} minutes. Last run{' '}
      {formatRelative(sync.last?.ranAt ?? null)}
      {sync.last && sync.last.status === 'ok'
        ? ` — ${sync.last.fetched} page${sync.last.fetched === 1 ? '' : 's'} (${sync.last.accepted} signed, ${sync.last.unsigned} unsigned), ${sync.last.created} new.`
        : '.'}
    </p>
  );
}

interface RowProps {
  contract: SignedContract;
  orgs: OrgLite[] | null;
  busy: boolean;
  error: string | null;
  canRelink: boolean;
  onPatch: (body: Record<string, unknown>) => void;
  onCreateProject: () => void;
  onAddClient: () => void;
}

function ContractRow({
  contract: c,
  orgs,
  busy,
  error,
  canRelink,
  onPatch,
  onCreateProject,
  onAddClient,
}: RowProps): React.ReactElement {
  return (
    <>
      <tr className="border-t border-[var(--color-border)] align-top">
        <td className="py-3 pl-3 pr-0 align-middle">
          {/* Click to star. Most of the unsigned side will never be
              signed, so this is how a quote worth chasing gets out of
              a 188-row list and into a short one. */}
          <button
            type="button"
            aria-label={c.followUp ? `Unstar ${c.name}` : `Star ${c.name}`}
            aria-pressed={c.followUp}
            title={c.followUp ? 'Following up — click to clear' : 'Star to follow up'}
            disabled={busy}
            onClick={() => onPatch({ followUp: !c.followUp })}
            className="rounded p-1 transition hover:bg-[var(--color-surface)] disabled:cursor-not-allowed"
          >
            <Star
              className={`h-4 w-4 ${
                c.followUp
                  ? 'fill-amber-400 text-amber-500'
                  : 'text-[var(--color-border)] hover:text-[var(--color-muted)]'
              }`}
            />
          </button>
        </td>
        <td className="px-3 py-3">
          <div className="font-medium text-[var(--color-header)]">{c.name}</div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-[var(--color-muted)]">
            {/* Prefer the PDF we hold — it survives whatever Qwilr does
                later — and fall back to Qwilr's rendered one, which the
                sync gives us for every contract. */}
            {c.attachmentId ? (
              <a
                href={`/api/organizations/${c.organizationId}/attachments/${c.attachmentId}/download`}
                target="_blank"
                rel="noreferrer"
                className="text-[var(--color-t3-blue)] hover:underline"
              >
                PDF
              </a>
            ) : (
              c.pdfUrl && (
                <a
                  href={c.pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--color-t3-blue)] hover:underline"
                >
                  PDF
                </a>
              )
            )}
            {c.shortlink && (
              <a
                href={c.shortlink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[var(--color-t3-blue)] hover:underline"
              >
                Qwilr <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </td>

        <td className="px-3 py-3">
          {c.organizationId ? (
            <Link
              to={`/organizations/${c.organizationId}`}
              className="text-[var(--color-t3-blue)] hover:underline"
            >
              {c.organizationName}
            </Link>
          ) : (
            <div className="space-y-1">
              <div className="text-xs text-amber-700">No client yet</div>
              <select
                aria-label={`Link ${c.name} to a client`}
                value=""
                disabled={busy || orgs === null}
                onChange={(e) => {
                  if (e.target.value === '__new') onAddClient();
                  else if (e.target.value) onPatch({ organizationId: e.target.value });
                }}
                className="w-full max-w-[160px] rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 py-1 text-xs"
              >
                <option value="">Link a client…</option>
                {(orgs ?? []).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
                {/* A brand-new client has nothing to pick, and leaving
                    the page to create one loses your place in the list. */}
                <option value="__new">+ Add a new client…</option>
              </select>
            </div>
          )}
          {c.organizationId && canRelink && (
            <select
              aria-label={`Change client for ${c.name}`}
              value={c.organizationId}
              disabled={busy || orgs === null}
              onChange={(e) => onPatch({ organizationId: e.target.value })}
              className="mt-1 w-full max-w-[160px] rounded-[var(--radius-button)] border border-transparent bg-transparent px-0 py-0 text-[10px] text-[var(--color-muted)] hover:border-[var(--color-border)]"
            >
              {(orgs ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          )}
        </td>

        <td className="px-3 py-3 whitespace-nowrap text-[var(--color-muted)]">
          {formatDate(c.acceptedAt ?? c.qwilrCreatedAt)}
        </td>

        <td className="px-3 py-3">
          <select
            aria-label={`Stage for ${c.name}`}
            value={c.stage}
            disabled={busy}
            onChange={(e) => onPatch({ stage: e.target.value })}
            className={`rounded-full px-2 py-1 text-xs font-medium ${STAGE_PILL[c.stage]}`}
          >
            {STAGES.map((s) => (
              <option key={s} value={s}>
                {STAGE_LABEL[s]}
              </option>
            ))}
          </select>
        </td>

        <td className="px-3 py-3">
          <select
            aria-label={`Work type for ${c.name}`}
            value={c.workType ?? ''}
            disabled={busy}
            onChange={(e) => onPatch({ workType: e.target.value || null })}
            className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 py-1 text-xs"
          >
            <option value="">Not set</option>
            <option value="block_hours">{WORK_TYPE_LABEL.block_hours}</option>
            <option value="project">{WORK_TYPE_LABEL.project}</option>
            <option value="mnda">{WORK_TYPE_LABEL.mnda}</option>
          </select>
        </td>

        <td className="px-3 py-3">
          {c.projectId ? (
            <Link
              to={`/projects/${c.projectId}`}
              className="text-[var(--color-t3-blue)] hover:underline"
            >
              {c.projectName}
            </Link>
          ) : c.stage === 'unsigned' ? (
            // Nothing to deliver until a client signs. Offering "Create
            // project" on a quote that may never be accepted would
            // manufacture budgets for work nobody has bought.
            <span className="text-xs text-[var(--color-muted)]">Awaiting signature</span>
          ) : c.workType === 'block_hours' ? (
            // Short on purpose — these three strings set this column's
            // width, and it was the one pushed off the edge. The full
            // sentence lives in the tooltip.
            <span className="text-xs text-[var(--color-muted)]" title="Draws down the client's block hours">
              Block hours
            </span>
          ) : c.workType === 'mnda' ? (
            // An MNDA is signed and filed. There is nothing to deliver,
            // so offering "Create project" here would be a trap.
            <span className="text-xs text-[var(--color-muted)]" title="Signed and filed — no work to deliver">
              Filed, no work
            </span>
          ) : (
            <Button
              variant="outline"
              className="!px-3 !py-1 !text-xs"
              disabled={busy}
              onClick={onCreateProject}
            >
              Create project
            </Button>
          )}
        </td>
      </tr>
      {error && (
        <tr className="border-0">
          <td colSpan={7} className="px-3 pb-3 text-xs text-[var(--color-danger)]">
            {error}
          </td>
        </tr>
      )}
    </>
  );
}

interface DialogProps {
  contract: SignedContract;
  orgs: OrgLite[];
  onClose: () => void;
  onCreated: () => void;
}

function CreateProjectDialog({
  contract,
  orgs,
  onClose,
  onCreated,
}: DialogProps): React.ReactElement {
  const [name, setName] = useState(contract.name);
  const [hours, setHours] = useState('');
  const [billingMode, setBillingMode] = useState<'flat_fee' | 'block_hours'>('flat_fee');
  const [orgId, setOrgId] = useState(contract.organizationId ?? '');
  const [sow, setSow] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    const budgetedHours = Number(hours);
    if (!name.trim()) {
      setError('Project name is required.');
      return;
    }
    if (!orgId) {
      setError('Choose the client this project belongs to.');
      return;
    }
    if (!Number.isFinite(budgetedHours) || budgetedHours < 0) {
      setError('Budgeted hours must be zero or more.');
      return;
    }
    setSaving(true);
    try {
      await apiFetch<{ project: Project }>(`/api/contracts/${contract.id}/project`, {
        method: 'POST',
        json: {
          name: name.trim(),
          budgetedHours,
          billingMode,
          organizationId: orgId,
          sowSummary: sow.trim() || null,
        },
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create the project.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Create project from contract"
    >
      <Card className="w-full max-w-lg space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-[var(--color-header)]">Create project</h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            From the signed contract &ldquo;{contract.name}&rdquo;. The contract moves to{' '}
            <strong>In progress</strong> and hours logged on the project&apos;s tickets count
            against this budget.
          </p>
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
            Project name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2.5 text-[15px]"
          />
        </label>

        <Select
          label="Client"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          disabled={Boolean(contract.organizationId)}
          hint={
            contract.organizationId
              ? 'Taken from the contract. Change it on the contract row first if this is wrong.'
              : 'This contract has no client linked yet.'
          }
        >
          <option value="">Choose a client…</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
              Budgeted hours
            </span>
            <input
              type="number"
              min="0"
              step="0.5"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="e.g. 40"
              className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2.5 text-[15px]"
            />
          </label>
          <Select
            label="Billing"
            value={billingMode}
            onChange={(e) => setBillingMode(e.target.value as 'flat_fee' | 'block_hours')}
          >
            <option value="flat_fee">Flat fee</option>
            <option value="block_hours">Block hours</option>
          </Select>
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
            SOW summary <span className="font-normal text-[var(--color-muted)]">(optional)</span>
          </span>
          <textarea
            value={sow}
            onChange={(e) => setSow(e.target.value)}
            rows={4}
            placeholder="What was sold — paste the scope from the contract."
            className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
          />
        </label>

        {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving ? 'Creating…' : 'Create project'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

interface AddClientProps {
  contract: SignedContract;
  onClose: () => void;
  onCreated: (org: OrgLite) => void;
}

/**
 * Create a client and link it to this contract, without leaving the page.
 *
 * The Qwilr page name is offered as the starting point rather than used
 * as the name: it is usually something like
 * "T3 - CEG Solutions - Proposal Agreement", which contains the client
 * but is not the client. Whoever is looking at the contract can see
 * which part is the company; we should not guess at it — guessing is
 * what got the automatic matcher removed.
 */
function AddClientDialog({ contract, onClose, onCreated }: AddClientProps): React.ReactElement {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reused, setReused] = useState(false);

  async function submit() {
    setError(null);
    if (!name.trim()) {
      setError('Client name is required.');
      return;
    }
    setSaving(true);
    try {
      const r = await apiFetch<{
        organization: OrgLite;
        reusedExisting: boolean;
      }>(`/api/contracts/${contract.id}/client`, {
        method: 'POST',
        json: { name: name.trim(), domain: domain.trim() || null },
      });
      if (r.reusedExisting) setReused(true);
      onCreated(r.organization);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the client.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Add a client for this contract"
    >
      <Card className="w-full max-w-lg space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-[var(--color-header)]">Add a client</h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Creates the organization and links it to this contract.
          </p>
        </div>

        <div className="rounded-[var(--radius-button)] bg-[var(--color-surface)] p-3">
          <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
            Qwilr page name
          </div>
          <div className="mt-0.5 text-sm text-[var(--color-body)]">{contract.name}</div>
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
            Client name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="The company, not the page title"
            autoFocus
            className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2.5 text-[15px]"
          />
          <span className="mt-1 block text-xs text-[var(--color-muted)]">
            If a client with this name already exists, it is linked rather than duplicated.
          </span>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
            Email domain <span className="font-normal text-[var(--color-muted)]">(optional)</span>
          </span>
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="acme.com"
            className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2.5 text-[15px]"
          />
        </label>

        {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
        {reused && (
          <p className="text-sm text-[var(--color-muted)]">
            That client already existed — linked to it instead of creating a duplicate.
          </p>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving ? 'Adding…' : 'Add and link'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
